import { ref, toValue, watch } from 'vue';
import { expandValueSet } from '@/common/api/terminology.api';
import { useNotificationStore } from '@/stores/notification';

const compare = (field) => (a, b) => (a[field] ?? '').localeCompare(b[field] ?? '', undefined, { numeric: field === 'code' });

/**
 * Every code in a ValueSet, for pick lists. Replaces the DataProvider Concept queries.
 *
 * `source` is `{ url }` for a named ValueSet or `{ systems }` for whole code systems, or a
 * getter returning one; a getter reloads when it changes. $expand has no order, so `sort`
 * ('display' or 'code') puts one back. A failure leaves the list empty and says so once.
 *
 * @param {object|(() => object)} source
 * @param {{ sort?: 'display'|'code' }} [options]
 */
export function useTerminologyList(source, { sort } = {}) {
    const items = ref([]);
    const loading = ref(false);
    const error = ref(null);
    let requestToken = 0;

    const load = async () => {
        const token = ++requestToken;
        const query = toValue(source);
        if (!query?.url && !query?.systems?.length) {
            items.value = [];
            return;
        }

        loading.value = true;
        error.value = null;
        try {
            const result = await expandValueSet({ ...query, all: true });
            if (token !== requestToken) return;
            items.value = sort ? [...result].sort(compare(sort)) : result;
        } catch (e) {
            if (token !== requestToken) return;
            console.error('Terminology list failed to load', query, e);
            error.value = e;
            items.value = [];
            useNotificationStore().add({ message: 'Could not load a list of codes. Try reloading the page.', color: 'error' });
        } finally {
            if (token === requestToken) loading.value = false;
        }
    };

    watch(() => JSON.stringify(toValue(source)), load, { immediate: true });

    return { items, loading, error, reload: load };
}
